import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { useDiscount, computeDiscountedPrice } from "./discount";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
    const { discount } = useDiscount();
    const [items, setItems] = useState(() => {
        try { return JSON.parse(localStorage.getItem("tb_cart") || "[]"); } catch { return []; }
    });

    useEffect(() => { localStorage.setItem("tb_cart", JSON.stringify(items)); }, [items]);

    const add = useCallback((product, quantity = 1) => {
        setItems((prev) => {
            const ex = prev.find((i) => i.sku === product.sku);
            if (ex) return prev.map((i) => i.sku === product.sku ? { ...i, quantity: i.quantity + quantity } : i);
            // Keep the ORIGINAL price and the product's category so we can recompute the
            // discounted price at display time (site-wide promos may change while items
            // sit in the cart — backend is authoritative and will re-price on checkout).
            return [
                ...prev,
                {
                    sku: product.sku,
                    nombre: product.nombre,
                    foto: product.foto,
                    precio: product.precio_venta,
                    categoria: product.categoria || "",
                    vendedor_id: product.vendedor_id || null,
                    quantity,
                    us_shipping_cost: product.us_shipping_cost || 0,
                },
            ];
        });
    }, []);
    const remove = useCallback((sku) => setItems((p) => p.filter((i) => i.sku !== sku)), []);
    const setQty = useCallback((sku, q) => setItems((p) => p.map((i) => i.sku === sku ? { ...i, quantity: Math.max(1, q) } : i)), []);
    const clear = useCallback(() => setItems([]), []);

    // Effective per-item price after applying the active site-wide discount (if any).
    // Vendor items keep their original price (site-wide discounts do not apply to them).
    const effectivePrice = useCallback((item) => {
        const { final } = computeDiscountedPrice(
            { precio_venta: item.precio, categoria: item.categoria, vendedor_id: item.vendedor_id },
            discount,
        );
        return final;
    }, [discount]);

    // Original subtotal (no discount applied) — kept so the summary can show "Antes: $40".
    const originalTotal = useMemo(() => items.reduce((s, i) => s + i.precio * i.quantity, 0), [items]);
    // Discounted subtotal (site-wide promo applied) — this is what backend will charge.
    const total = useMemo(() => items.reduce((s, i) => s + effectivePrice(i) * i.quantity, 0), [items, effectivePrice]);
    const siteDiscountAmount = useMemo(() => Math.max(0, originalTotal - total), [originalTotal, total]);
    const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

    const value = useMemo(
        () => ({ items, add, remove, setQty, clear, total, originalTotal, siteDiscountAmount, effectivePrice, count }),
        [items, add, remove, setQty, clear, total, originalTotal, siteDiscountAmount, effectivePrice, count]
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
